'use client'

import React, { useMemo, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'

import { OtpPageContent } from '@/src/components/common/otpPage/otpPageContent'
import { useOtpFlow } from '@/src/hooks/useOtpFlow'
import useClevertapForSignin from '@/src/hooks/analytics/clevertap/signin'
import { isServer } from '@/src/utils/browser'

interface OtpPageWrapperProps {
    signInPage?: boolean
}

const OtpPageWrapper: React.FC<OtpPageWrapperProps> = ({ signInPage }) => {
    const router = useRouter()
    const { triggerSigninEvent } = useClevertapForSignin()
    const t = useTranslations('otpModal')

    const onClose = () => {
        if (signInPage && !isServer()) {
            const params = new URLSearchParams(window.location.search)
            const returnUrl = params.get('returnUrl')
            if (returnUrl) {
                router.push(returnUrl)
            } else {
                router.push('/')
            }
        } else {
            router.push('/')
        }
    }

    const otpFlow = useOtpFlow({
        onClose: onClose,
        onMenuClose: undefined,
        translate: t
    })

    // Track signin page open event - user is not logged in at this point
    useEffect(() => {
        triggerSigninEvent('Air_UI_Action', {
            action_name: 'signin_open',
            action_type: 'PAGE_VIEW',
            action_value: 'success'
        })
    }, [])

    // Prepare data for presentation component (memoized to prevent unnecessary re-renders)
    const flowData = useMemo(
        () => ({
            currentStep: otpFlow.currentStep,
            error: otpFlow.error,
            errorState: otpFlow.errorState,
            isWelcomeBack: otpFlow.isWelcomeBack,
            existingEmail: otpFlow.existingEmail,
            isGoogleFlow: otpFlow.isGoogleFlow,
            googleUserData: otpFlow.googleUserData,

            // Mobile data
            mobileCountryCode: otpFlow.mobileCountryCode,
            selectedCountryCode: otpFlow.selectedCountryCode,
            countryData: otpFlow.countryData,
            isCountryDataLoading: otpFlow.isCountryDataLoading,
            mobileNumber: otpFlow.mobileNumber,
            mobileOtp: otpFlow.mobileOtp,

            // Email data
            email: otpFlow.email,
            emailOtp: otpFlow.emailOtp,

            // Loading states
            isSendingOtp: otpFlow.isSendingOtp,
            isVerifyingOtp: otpFlow.isVerifyingOtp,

            // Timer states
            mobileResendTimer: otpFlow.mobileResendTimer,
            emailResendTimer: otpFlow.emailResendTimer
        }),
        [
            otpFlow.currentStep,
            otpFlow.error,
            otpFlow.errorState,
            otpFlow.isWelcomeBack,
            otpFlow.existingEmail,
            otpFlow.isGoogleFlow,
            otpFlow.googleUserData,
            otpFlow.mobileCountryCode,
            otpFlow.selectedCountryCode,
            otpFlow.countryData,
            otpFlow.isCountryDataLoading,
            otpFlow.mobileNumber,
            otpFlow.mobileOtp,
            otpFlow.email,
            otpFlow.emailOtp,
            otpFlow.isSendingOtp,
            otpFlow.isVerifyingOtp,
            otpFlow.mobileResendTimer,
            otpFlow.emailResendTimer
        ]
    )

    // Handlers are already stable (useCallback in the hook)
    const flowHandlers = {
        onClose: onClose,

        // Mobile handlers
        handleCountryCodeChange: otpFlow.handleCountryCodeChange,
        handleMobileNumberChange: otpFlow.handleMobileNumberChange,
        handleMobileOtpChange: otpFlow.handleMobileOtpChange,
        handleSendMobileOtp: otpFlow.handleSendMobileOtp,
        handleVerifyMobileOtp: otpFlow.handleVerifyMobileOtp,
        handleResendMobileOtp: otpFlow.handleResendMobileOtp,
        handleBackToMobileInput: otpFlow.handleBackToMobileInput,

        // Email handlers
        handleEmailChange: otpFlow.handleEmailChange,
        handleEmailOtpChange: otpFlow.handleEmailOtpChange,
        handleSendEmailOtp: otpFlow.handleSendEmailOtp,
        handleVerifyEmailOtp: otpFlow.handleVerifyEmailOtp,
        handleResendEmailOtp: otpFlow.handleResendEmailOtp,
        handleBackToEmailInput: otpFlow.handleBackToEmailInput,

        // Google Sign-In handlers
        handleGoogleSignIn: otpFlow.handleGoogleSignIn,
        handleGoogleSignInError: otpFlow.handleGoogleSignInError,
        handleSendGoogleMobileOtp: otpFlow.handleSendGoogleMobileOtp,
        handleVerifyGoogleMobileOtp: otpFlow.handleVerifyGoogleMobileOtp,
        handleResendGoogleMobileOtp: otpFlow.handleResendGoogleMobileOtp
    }

    return <OtpPageContent flowData={flowData} flowHandlers={flowHandlers} />
}

export { OtpPageWrapper }
export default OtpPageWrapper
